import React, { useEffect, useMemo, useState } from 'react';
import { Card, DatePicker, Select, Space, Tabs, Table, Typography, Empty, Spin, message, Switch } from 'antd';
import { TableOutlined, BarChartOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { useQuery } from 'react-query';
import GanttChart from '../../components/GanttChart';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

/**
 * 项目分析页面（设计负责人视角）
 * 功能：
 * 1) 仅加载用户有权限查看的项目（后端会根据角色与 designLeaderId 过滤）
 * 2) 支持选择项目与日期范围，按"任务"与"执行人"两个维度细分统计工作量（工时、日志数）
 * 3) 列表视图中，提供可展开的子表展示更细的拆分维度
 */
const ProjectAnalysis = () => {
  // 过滤条件
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(undefined);
  const [dateRange, setDateRange] = useState([dayjs().subtract(29, 'day'), dayjs()]);

  // 数据与状态
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logs, setLogs] = useState([]);
  
  // 视图模式：true为甘特图，false为列表
  const [isGanttView, setIsGanttView] = useState(true);

  // 使用React Query监听项目数据变化
  const { data: projectData } = useQuery(
    ['project', projectId],
    () => projectId ? axios.get(`/projects/${projectId}`).then(res => res.data) : null,
    {
      enabled: !!projectId,
      staleTime: 0, // 确保数据实时更新
    }
  );

  // 初始化加载可见项目
  useEffect(() => {
    const loadProjects = async () => {
      setLoadingProjects(true);
      try {
        const res = await axios.get('/projects', { params: { limit: 1000 } });
        const list = res?.data?.data?.projects || [];
        setProjects(list);
        // 默认选中第一个项目（若存在）
        if (list.length > 0) {
          setProjectId(list[0].id);
        }
      } catch (err) {
        console.error(err);
        message.error('加载项目列表失败');
      } finally {
        setLoadingProjects(false);
      }
    };
    loadProjects();
  }, []);

  // 当项目数据变化时，动态设置日期范围
  useEffect(() => {
     const updateDateRangeForProject = async () => {
       if (!projectId || !projectData?.data?.project) return;
       
       try {
        // 使用React Query获取的项目数据
        const project = projectData.data.project;
        const tasks = project?.tasks || [];
        
        if (tasks.length === 0) {
          // 如果没有任务，使用默认的近30天范围
          setDateRange([dayjs().subtract(29, 'day'), dayjs()]);
          return;
        }
        
        // 找出所有任务的最早开始日期和最晚截止日期
        let earliestStart = null;
        let latestDue = null;
        
        tasks.forEach(task => {
          if (task.startDate) {
            const startDate = dayjs(task.startDate);
            if (!earliestStart || startDate.isBefore(earliestStart)) {
              earliestStart = startDate;
            }
          }
          if (task.dueDate) {
            const dueDate = dayjs(task.dueDate);
            if (!latestDue || dueDate.isAfter(latestDue)) {
              latestDue = dueDate;
            }
          }
        });
        
        // 如果找到了有效的日期范围
        if (earliestStart && latestDue) {
          const today = dayjs();
          const totalDays = latestDue.diff(earliestStart, 'day') + 1;
          
          // 如果当天不在任务日期范围内，扩展范围使当天在中间位置
          if (today.isBefore(earliestStart) || today.isAfter(latestDue)) {
            // 计算需要的总天数，确保当天在中间
            const daysToToday = Math.max(
              Math.abs(today.diff(earliestStart, 'day')),
              Math.abs(today.diff(latestDue, 'day'))
            );
            const extendedRange = daysToToday * 2;
            
            const newStart = today.subtract(Math.floor(extendedRange / 2), 'day');
            const newEnd = today.add(Math.ceil(extendedRange / 2), 'day');
            
            // 确保包含原始任务范围（去掉 dayjs.min/max 依赖，手动比较以兼容未加载 minMax 插件的环境）
            const finalStart = newStart.isBefore(earliestStart) ? newStart : earliestStart;
            const finalEnd = newEnd.isAfter(latestDue) ? newEnd : latestDue;
            
            setDateRange([finalStart, finalEnd]);
          } else {
            // 当天在任务范围内，检查是否需要调整以使当天更居中
            const daysFromStart = today.diff(earliestStart, 'day');
            const daysToEnd = latestDue.diff(today, 'day');
            
            if (Math.abs(daysFromStart - daysToEnd) > totalDays * 0.3) {
              // 如果当天偏离中心较多，调整范围
              const halfRange = Math.floor(totalDays / 2);
              const newStart = today.subtract(halfRange, 'day');
              const newEnd = today.add(halfRange, 'day');
              
              // 确保包含原始任务范围（去掉 dayjs.min/max 依赖，手动比较以兼容未加载 minMax 插件的环境）
              const finalStart = newStart.isBefore(earliestStart) ? newStart : earliestStart;
              const finalEnd = newEnd.isAfter(latestDue) ? newEnd : latestDue;
              
              setDateRange([finalStart, finalEnd]);
            } else {
              // 当天位置合适，直接使用任务范围
              setDateRange([earliestStart, latestDue]);
            }
          }
        } else {
          // 如果没有找到有效的任务日期，使用默认范围
          setDateRange([dayjs().subtract(29, 'day'), dayjs()]);
        }
      } catch (err) {
        console.error('获取项目任务日期范围失败:', err);
        // 出错时使用默认范围
        setDateRange([dayjs().subtract(29, 'day'), dayjs()]);
      }
    };
    
    updateDateRangeForProject();
  }, [projectId, projectData]); // 监听projectData变化

  // 根据过滤条件加载日志（按项目 + 日期范围）
  useEffect(() => {
    const loadLogs = async () => {
      if (!projectId || !dateRange || dateRange.length !== 2) return;
      setLoadingLogs(true);
      try {
        const [start, end] = dateRange;
        const params = {
          projectId,
          startDate: start.format('YYYY-MM-DD'),
          endDate: end.format('YYYY-MM-DD'),
          limit: 2000
        };
        const res = await axios.get('/daily-logs', { params });
        const list = res?.data?.data?.logs || [];
        setLogs(list);
      } catch (err) {
        console.error(err);
        message.error('加载工作日志失败');
      } finally {
        setLoadingLogs(false);
      }
    };
    loadLogs();
  }, [projectId, dateRange]);

  // 工具：聚合函数
  const aggregateByTask = useMemo(() => {
    /**
     * 按任务聚合
     * 返回：[{ key, taskId, taskTitle, totalHours, logCount, assignees: [{ userId, realName, hours, logCount }] }]
     */
    const taskMap = new Map();
    logs.forEach((log) => {
      const t = log?.task;
      const u = log?.user;
      const hours = Number(log?.workHours || 0);
      if (!t) return;
      const taskKey = t.id;
      if (!taskMap.has(taskKey)) {
        taskMap.set(taskKey, {
          key: `task-${t.id}`,
          taskId: t.id,
          taskTitle: t.title,
          totalHours: 0,
          logCount: 0,
          assignees: new Map()
        });
      }
      const rec = taskMap.get(taskKey);
      rec.totalHours += hours;
      rec.logCount += 1;
      if (u) {
        if (!rec.assignees.has(u.id)) {
          rec.assignees.set(u.id, { key: `task-${t.id}-user-${u.id}`, userId: u.id, realName: u.realName, hours: 0, logCount: 0 });
        }
        const a = rec.assignees.get(u.id);
        a.hours += hours;
        a.logCount += 1;
      }
    });
    return Array.from(taskMap.values()).map((item) => ({
      ...item,
      assignees: Array.from(item.assignees.values())
    }));
  }, [logs]);

  const aggregateByUser = useMemo(() => {
    /**
     * 按执行人聚合
     * 返回：[{ key, userId, realName, totalHours, logCount, tasks: [{ taskId, taskTitle, hours, logCount }] }]
     */
    const userMap = new Map();
    logs.forEach((log) => {
      const t = log?.task;
      const u = log?.user;
      const hours = Number(log?.workHours || 0);
      if (!u) return;
      const userKey = u.id;
      if (!userMap.has(userKey)) {
        userMap.set(userKey, {
          key: `user-${u.id}`,
          userId: u.id,
          realName: u.realName,
          totalHours: 0,
          logCount: 0,
          tasks: new Map()
        });
      }
      const rec = userMap.get(userKey);
      rec.totalHours += hours;
      rec.logCount += 1;
      if (t) {
        if (!rec.tasks.has(t.id)) {
          rec.tasks.set(t.id, { key: `user-${u.id}-task-${t.id}`, taskId: t.id, taskTitle: t.title, hours: 0, logCount: 0 });
        }
        const tk = rec.tasks.get(t.id);
        tk.hours += hours;
        tk.logCount += 1;
      }
    });
    return Array.from(userMap.values()).map((item) => ({
      ...item,
      tasks: Array.from(item.tasks.values())
    }));
  }, [logs]);

  // 列定义
  const taskColumns = [
    {
      title: '任务名称',
      dataIndex: 'taskTitle',
      key: 'taskTitle',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: '工时合计',
      dataIndex: 'totalHours',
      key: 'totalHours',
      render: (v) => <Text>{(Number(v) || 0).toFixed(1)}</Text>
    },
    {
      title: '日志条数',
      dataIndex: 'logCount',
      key: 'logCount'
    }
  ];

  const taskAssigneeColumns = [
    { title: '执行人', dataIndex: 'realName', key: 'realName' },
    { title: '工时', dataIndex: 'hours', key: 'hours', render: (v) => (Number(v) || 0).toFixed(1) },
    { title: '日志', dataIndex: 'logCount', key: 'logCount' }
  ];

  const userColumns = [
    {
      title: '执行人',
      dataIndex: 'realName',
      key: 'realName',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: '工时合计',
      dataIndex: 'totalHours',
      key: 'totalHours',
      render: (v) => <Text>{(Number(v) || 0).toFixed(1)}</Text>
    },
    { title: '日志条数', dataIndex: 'logCount', key: 'logCount' }
  ];

  const userTaskColumns = [
    { title: '任务名称', dataIndex: 'taskTitle', key: 'taskTitle' },
    { title: '工时', dataIndex: 'hours', key: 'hours', render: (v) => (Number(v) || 0).toFixed(1) },
    { title: '日志', dataIndex: 'logCount', key: 'logCount' }
  ];

  const currentProject = useMemo(() => projects.find(p => p.id === projectId), [projects, projectId]);

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ marginBottom: 8 }}>项目分析</Title>
          <Text type="secondary">设计负责人可以查看自己负责项目的工作量统计，并按任务、执行人和执行时间进行细分</Text>
        </div>

        <Card>
          <Space wrap align="center" size={[16, 12]}>
            <div>
              <Text type="secondary" style={{ marginRight: 8 }}>项目</Text>
              <Select
                style={{ width: 320 }}
                placeholder={loadingProjects ? '加载中…' : '请选择项目'}
                loading={loadingProjects}
                value={projectId}
                onChange={setProjectId}
                options={projects.map(p => ({ value: p.id, label: p.name }))}
                showSearch
                optionFilterProp="label"
              />
            </div>
            <div>
              <Text type="secondary" style={{ marginRight: 8 }}>日期范围</Text>
              <RangePicker
                value={dateRange}
                onChange={(v) => setDateRange(v || [dayjs().subtract(29, 'day'), dayjs()])}
                allowClear={false}
              />
            </div>
            {currentProject && (
              <div>
                <Text type="secondary">当前项目：</Text>
                <Text>{currentProject.name}</Text>
              </div>
            )}
            <div style={{ marginLeft: 'auto' }}>
              <Space align="center">
                <TableOutlined style={{ color: !isGanttView ? '#1890ff' : '#8c8c8c' }} />
                <Switch 
                  checked={isGanttView}
                  onChange={setIsGanttView}
                  size="small"
                />
                <BarChartOutlined style={{ color: isGanttView ? '#1890ff' : '#8c8c8c' }} />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {isGanttView ? '甘特图' : '列表'}
                </Text>
              </Space>
            </div>
          </Space>
        </Card>

        <Card>
          {(!projectId || loadingLogs) && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
              {loadingLogs ? (
                <Spin size="large">
                  <div style={{ padding: '50px' }}>
                    <div style={{ textAlign: 'center', color: '#666' }}>数据加载中...</div>
                  </div>
                </Spin>
              ) : (
                <Empty description="请选择项目与日期范围" />
              )}
            </div>
          )}

          {projectId && !loadingLogs && (
            isGanttView ? (
              // 甘特图视图
              <div>
                <div style={{ marginBottom: 16, padding: '0 16px' }}>
                  <Text strong style={{ fontSize: '16px' }}>甘特图视图</Text>
                  <Text type="secondary" style={{ marginLeft: 16 }}>
                    显示任务和执行人的工作时间分布
                  </Text>
                </div>
                <GanttChart logs={logs} dateRange={dateRange} />
              </div>
            ) : (
              // 列表视图
              <Tabs
                defaultActiveKey="byTask"
                items={[
                  {
                    key: 'byTask',
                    label: '按任务',
                    children: (
                      <Table
                        rowKey="key"
                        dataSource={aggregateByTask}
                        columns={taskColumns}
                        expandable={{
                          expandedRowRender: (record) => (
                            <Table
                              size="small"
                              pagination={false}
                              rowKey="key"
                              dataSource={record.assignees}
                              columns={taskAssigneeColumns}
                            />
                          )
                        }}
                        pagination={{ pageSize: 10 }}
                      />
                    )
                  },
                  {
                    key: 'byUser',
                    label: '按执行人',
                    children: (
                      <Table
                        rowKey="key"
                        dataSource={aggregateByUser}
                        columns={userColumns}
                        expandable={{
                          expandedRowRender: (record) => (
                            <Table
                              size="small"
                              pagination={false}
                              rowKey="key"
                              dataSource={record.tasks}
                              columns={userTaskColumns}
                            />
                          )
                        }}
                        pagination={{ pageSize: 10 }}
                      />
                    )
                  }
                ]}
              />
            )
          )}
        </Card>
      </Space>
    </div>
  );
};

export default ProjectAnalysis;